import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import VerticalRangeCalendar from '../VerticalRangeCalendar';
import './DateRangePicker.css';

interface DateRangePickerProps {
    startDate: string;
    endDate: string;
    onChange: (startDate: string, endDate: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    hideSummary?: boolean;
    showIconAlways?: boolean;
}

const MONTHS = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate,
    endDate,
    onChange,
    placeholder = "ASAP",
    className = "",
    disabled = false,
    hideSummary = false,
    showIconAlways = false
}) => {


    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setIsFadingOut(false);
        } else if (isVisible) {
            setIsFadingOut(true);
            const timer = setTimeout(() => {
                setIsVisible(false);
                setIsFadingOut(false);
            }, 300); // Match CSS animation duration
            return () => clearTimeout(timer);
        }
    }, [isOpen, isVisible]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideContainer = containerRef.current && containerRef.current.contains(target);
            const isClickInsidePopover = popoverRef.current && popoverRef.current.contains(target);
            
            if (!isClickInsideContainer && !isClickInsidePopover) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.classList.add('date-picker-open');
        } else {
            document.body.classList.remove('date-picker-open');
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.classList.remove('date-picker-open');
        };
    }, [isOpen]);

    const getDateRangeSummary = (startVal: string, endVal: string) => {
        if (!startVal) return placeholder;
        
        const formatShortDate = (dateStr: string) => {
            if (!dateStr || !dateStr.includes('-')) return dateStr;
            const parts = dateStr.split('-');
            const yearStr = parts[0].substring(2);
            const mStr = parseInt(parts[1]).toString();
            const dStr = parseInt(parts[2]).toString();
            return `${mStr}/${dStr}/${yearStr}`;
        };

        const sLabel = formatShortDate(startVal);

        if (!endVal || startVal === endVal) {
            return sLabel;
        }

        const eLabel = formatShortDate(endVal);

        return `${sLabel} - ${eLabel}`;
    };

    const handleTriggerClick = (e: React.MouseEvent) => {
        if (disabled) return;
        setIsOpen(!isOpen);
    };

    return (
        <div className={`date-range-picker-container ${className} ${disabled ? 'disabled' : ''}`} ref={containerRef}>
            <button 
                type="button"
                className={`date-range-picker-trigger ${isOpen ? 'active' : ''} ${startDate || placeholder === 'ASAP' ? 'has-value' : ''} ${hideSummary ? 'hide-summary' : ''}`}
                onClick={handleTriggerClick}
                disabled={disabled}
            >
                <div className="trigger-content">
                    {(showIconAlways || !startDate) && (
                        <svg className="calendar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                    )}
                    {!hideSummary && (
                        <span className="summary-text">{getDateRangeSummary(startDate, endDate)}</span>
                    )}
                </div>
            </button>

            {isVisible && createPortal(
                <>
                    <div className={`date-range-picker-overlay ${isFadingOut ? 'blur-out' : ''}`} onClick={() => setIsOpen(false)} />
                    <div className={`date-range-picker-popover ${isFadingOut ? 'pop-out' : ''}`} ref={popoverRef}>
                        <div className="popover-header">
                            <h3>Select Dates</h3>
                            <div className="popover-header-actions">
                                <button className="clear-dates" onClick={() => onChange('', '')}>Clear</button>
                                <button className="close-popover" onClick={() => setIsOpen(false)}>Done</button>
                            </div>
                        </div>

                        <VerticalRangeCalendar
                            startDate={startDate}
                            endDate={endDate}
                            onChange={onChange}
                        />
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default DateRangePicker;

